import { useEffect, useState, useCallback, useMemo } from "react";
import { IoClose } from "react-icons/io5";
import { LuEye, LuEyeOff } from "react-icons/lu";
import { useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { addUser, resetAddUser } from "../../redux/users/usersSlice";
import toast from "react-hot-toast";
import toastOptions from "../../config/toastOptions";
import CustomSelect from "../common/customSelect";
import CustomInput from "../common/customInput";
import { useTranslation } from "react-i18next";
import { humanizeError } from "../../utils/errorText";

const AddUser = ({ open, onClose, roles, users }) => {
  const { t } = useTranslation();

  const branchUrl = useParams().id;
  const [role, setRole] = useState("");
  const [currentUser, setCurrentUser] = useState({});
  const [duplicateName, setDuplicateName] = useState(false);
  const [duplicatePin, setDuplicatePin] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const ToastOptions = useMemo(() => toastOptions, []);
  const [loadingToastId, setLoadingToastId] = useState(null);
  const dispatch = useDispatch();

  const usersReducer = useSelector((state) => state.users);
  const { loading, success, error } = usersReducer.addUser;

  const checkUser = (name) => {
    return users.some(
      (item) => item.name.toLowerCase() === name.toLowerCase()
    );
  };

  const checkPin = (pin) => {
    return users.some((item) => String(item.pinCode) === String(pin));
  };

  const resetState = useCallback(() => {
    setCurrentUser({});
    setRole("");
    setLoadingToastId(null);
    setDuplicateName(false);
    setDuplicatePin(false);
    setShowPin(false);
  }, []);

  const handleClose = useCallback(() => {
    onClose();
    resetState();
  }, [onClose, resetState]);

  const findRoleName = useCallback(
    (roleId) => {
      const found = roles.find((item) => item.id === roleId);
      return found ? { label: found.name, value: roleId } : null;
    },
    [roles]
  );

  const formatRoles = () =>
    roles.map((item) => ({ label: item.name, value: item.id }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (
      !currentUser.name ||
      !currentUser.pinCode ||
      !role ||
      duplicateName ||
      duplicatePin
    ) {
      return;
    }
    dispatch(
      addUser({
        branchUrl,
        userName: currentUser.name,
        pinCode: currentUser.pinCode,
        roleName: findRoleName(currentUser.userRoleId).label,
        roleId: currentUser.userRoleId,
      })
    );
  };

  useEffect(() => {
    if (loading && loadingToastId === null) {
      setLoadingToastId(
        toast.loading(t("nav.createUserP"), { position: "top-right" })
      );
    } else if (success) {
      if (loadingToastId) {
        toast.dismiss(loadingToastId);
      }
      toast.success(t("nav.createUserM"), ToastOptions);
      handleClose();
      resetState();
      dispatch(resetAddUser());
    } else if (error) {
      if (loadingToastId) {
        toast.dismiss(loadingToastId);
      }
      toast.error(humanizeError(error, t), ToastOptions);
      dispatch(resetAddUser());
    }
  }, [
    ToastOptions,
    dispatch,
    error,
    handleClose,
    loading,
    loadingToastId,
    resetState,
    success,
    t,
  ]);

  useEffect(() => {
    return () => {
      resetState();
      dispatch(resetAddUser());
    };
  }, [dispatch, resetState]);

  const setPinVal = (e) => {
    const digits = e.target.value.replace(/\D/g, "");
    if (digits.length > 20) return;
    setCurrentUser((prevUser) => ({
      ...prevUser,
      pinCode: digits,
    }));
    setDuplicatePin(checkPin(digits));
  };

  return (
    <div
      className={`fixed inset-0 flex justify-center items-center transition-colors p-[2%] z-[1000] ${
        open ? "visible bg-black/20" : "invisible"
      }`}
      aria-hidden={!open}
    >
      <div
        className={`bg-[--btn-txt] w-full max-w-[35rem] rounded-xl shadow p-6 transition-all max-h-[80dvh] overflow-y-auto border border-solid border-[--gr-4] ${
          open ? "scale-100 opacity-100" : "scale-125 opacity-0"
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-user-title"
      >
        {!loading && (
          <span className="flex justify-end text-2xl text-[--gr-6]">
            <button
              type="button"
              onClick={handleClose}
              aria-label={t("common.close")}
              className="rounded-full p-1 text-4xl cursor-pointer bg-transparent flex items-center hover:bg-[--gr-0] hover:text-[--gr-6]"
            >
              <IoClose aria-hidden="true" />
            </button>
          </span>
        )}

        <div
          id="add-user-title"
          className="text-[--gr-5] font-bold text-lg text-center"
        >
          {t("addUser.add")}
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label
              htmlFor="add-user-name"
              className="block mb-2 text-sm font-medium text-[--gr-4]"
            >
              {t("addUser.name")}:
            </label>
            <CustomInput
              type="text"
              id="add-user-name"
              value={currentUser.name ? currentUser.name : ""}
              onChange={(e) => {
                setCurrentUser((prevUser) => ({
                  ...prevUser,
                  name: e.target.value,
                }));
                setDuplicateName(checkUser(e.target.value));
              }}
              className="border-solid border-[0.5px] border-[--gr-4] text-[--gr-9] text-sm rounded-lg block w-full p-2.5"
              placeholder={t("addUser.name")}
              required
              disabled={loading}
              autoComplete="off"
            />
            {duplicateName && (
              <p className="text-xs text-red-600">{t("addUser.nameMsg")}</p>
            )}
          </div>

          <div className="mb-4">
            <label
              htmlFor="add-user-pin"
              className="block mb-2 text-sm font-medium text-[--gr-4]"
            >
              {t("addUser.pin")}:
            </label>
            <div className="relative">
              <CustomInput
                type={showPin ? "text" : "password"}
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="new-password"
                id="add-user-pin"
                value={currentUser.pinCode ? currentUser.pinCode : ""}
                onChange={setPinVal}
                minLength={4}
                maxLength={20}
                className="border-solid border-[0.5px] border-[--gr-4] text-[--gr-9] text-sm rounded-lg block w-full p-2.5 pr-12"
                placeholder={t("addUser.pin")}
                required
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPin((prev) => !prev)}
                aria-label={showPin ? t("common.hidePin") : t("common.showPin")}
                aria-pressed={showPin}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xl text-[--gr-4] hover:text-[--primary] bg-transparent flex items-center"
              >
                {showPin ? (
                  <LuEyeOff aria-hidden="true" />
                ) : (
                  <LuEye aria-hidden="true" />
                )}
              </button>
            </div>
            {duplicatePin && (
              <p className="text-xs text-red-600">{t("addUser.pinMsg")}</p>
            )}
          </div>

          <div className="mb-4">
            <label
              htmlFor="add-user-role"
              className="block mb-2 text-sm font-medium text-[--gr-4]"
            >
              {t("addUser.role")}:
            </label>
            <CustomSelect
              id="add-user-role"
              value={findRoleName(role) ? findRoleName(role) : ""}
              onChange={(selectedOption) => {
                setRole(selectedOption.value);
                setCurrentUser((prevUser) => ({
                  ...prevUser,
                  userRoleId: selectedOption.value,
                }));
              }}
              options={formatRoles()}
              isDisabled={loading}
            />
          </div>

          <div className="flex gap-4">
            <button
              className="w-full px-4 py-3 rounded-lg bg-[--gr-1] text-[#ffffff] hover:opacity-80 transition disabled:opacity-50"
              type="button"
              onClick={handleClose}
              disabled={loading}
            >
              {t("addUser.cancel")}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="w-full px-4 py-3 rounded-lg bg-[--primary] text-[#ffffff] hover:opacity-80 transition disabled:opacity-50"
            >
              {loading ? t("common.loading") : t("addUser.save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddUser;
